import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { commentService } from '@services';

// Get comments for a task
export const useTaskComments = (taskId, params = {}, options = {}) => {
  return useQuery({
    queryKey: ['comments', 'task', taskId, params],
    queryFn: () => commentService.getTaskComments(taskId, params),
    enabled: !!taskId,
    ...options,
  });
};

// Create comment (without attachments)
export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => commentService.create(data),
    onSuccess: (_, variables) => {
      // Use partial matching to invalidate all comment queries for this task regardless of params
      queryClient.invalidateQueries({
        queryKey: ['comments', 'task', variables.taskId],
        exact: false
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Create comment with attachments
export const useCreateCommentWithAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => commentService.createWithAttachments(data),
    onSuccess: (_, variables) => {
      // Use partial matching to invalidate all comment queries for this task regardless of params
      queryClient.invalidateQueries({
        queryKey: ['comments', 'task', variables.taskId],
        exact: false
      });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Add attachment to existing comment
export const useAddAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, file }) => commentService.addAttachment(commentId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

// Delete attachment
export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId) => commentService.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

// Update comment
export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => commentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

// Delete comment
export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => commentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};
